/**
 * 내보내기 유틸리티 함수
 * html2canvas와 jsPDF를 사용하여 캔버스를 PDF/JPG로 내보내기
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Shape } from '../types/Shape';
import {
  ExportOptions,
  PDFExportOptions,
  JPGExportOptions,
  BoundingBox,
  IMAGE_QUALITY_MAP,
  ExportProgress,
} from '../types/Export';

/**
 * 선택된 shapes의 바운딩 박스를 계산합니다.
 */
export function calculateBoundingBox(shapes: Shape[], padding: number = 20): BoundingBox {
  if (shapes.length === 0) {
    return { minX: 0, minY: 0, maxX: 100, maxY: 100, width: 100, height: 100 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  shapes.forEach((shape) => {
    const x = shape.x;
    const y = shape.y;
    let width: number;
    let height: number;

    if (shape.type === 'circle' && shape.radius) {
      width = shape.radius * 2;
      height = shape.radius * 2;
    } else if (shape.type === 'arrow') {
      // 화살표의 경우 시작점과 끝점을 고려
      const endX = x + (shape.width || 0);
      const endY = y + (shape.height || 0);
      minX = Math.min(minX, x, endX);
      minY = Math.min(minY, y, endY);
      maxX = Math.max(maxX, x, endX);
      maxY = Math.max(maxY, y, endY);
      return;
    } else {
      width = shape.width || 100;
      height = shape.height || 100;
    }

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  });

  // 패딩 적용
  minX -= padding;
  minY -= padding;
  maxX += padding;
  maxY += padding;

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * 뷰포트 영역을 계산합니다.
 */
export function calculateViewportBounds(
  stagePos: { x: number; y: number },
  stageScale: number,
  viewportWidth: number,
  viewportHeight: number
): BoundingBox {
  const minX = -stagePos.x / stageScale;
  const minY = -stagePos.y / stageScale;
  const width = viewportWidth / stageScale;
  const height = viewportHeight / stageScale;

  return {
    minX,
    minY,
    maxX: minX + width,
    maxY: minY + height,
    width,
    height,
  };
}

/**
 * 준비된 캔버스 요소와 cleanup 함수를 포함하는 결과 타입
 */
export interface PreparedCanvas {
  element: HTMLElement;
  cleanup: () => void;
}

/**
 * 내보내기를 위해 캔버스 요소를 임시로 준비합니다.
 * 유튜브/오디오 요소를 대체 이미지로 교체하는 등의 처리를 수행합니다.
 * @returns 준비된 요소와 cleanup 함수를 포함하는 객체
 */
export async function prepareCanvasForExport(
  container: HTMLElement,
  bounds: BoundingBox,
  stagePos: { x: number; y: number },
  stageScale: number
): Promise<PreparedCanvas> {
  // 원본 컨테이너를 복제
  const clonedContainer = container.cloneNode(true) as HTMLElement;

  // 임시로 document에 추가 (스타일 계산을 위해)
  clonedContainer.style.position = 'absolute';
  clonedContainer.style.left = '-9999px';
  clonedContainer.style.top = '-9999px';
  document.body.appendChild(clonedContainer);

  // 유튜브 iframe을 썸네일로 교체
  const youtubeIframes = clonedContainer.querySelectorAll('iframe[src*="youtube"]');
  youtubeIframes.forEach((iframe) => {
    const parent = iframe.parentElement;
    if (parent) {
      // 유튜브 URL에서 video ID 추출
      const src = iframe.getAttribute('src') || '';
      const videoIdMatch = src.match(/embed\/([^?]+)/);
      const videoId = videoIdMatch ? videoIdMatch[1] : '';

      if (videoId) {
        const thumbnail = document.createElement('div');
        thumbnail.style.width = '100%';
        thumbnail.style.height = '100%';
        thumbnail.style.backgroundImage = `url(https://img.youtube.com/vi/${videoId}/hqdefault.jpg)`;
        thumbnail.style.backgroundSize = 'cover';
        thumbnail.style.backgroundPosition = 'center';
        thumbnail.style.display = 'flex';
        thumbnail.style.alignItems = 'center';
        thumbnail.style.justifyContent = 'center';

        // 플레이 버튼 오버레이
        const playIcon = document.createElement('div');
        playIcon.innerHTML = `
          <svg width="68" height="48" viewBox="0 0 68 48" fill="none">
            <path d="M66.52 7.74c-.78-2.93-3.08-5.24-6.01-6.01C55.23.5 34 .5 34 .5S12.77.5 7.49 1.73c-2.93.77-5.23 3.08-6.01 6.01C.25 13.03.25 24 .25 24s0 10.97 1.23 16.26c.78 2.93 3.08 5.24 6.01 6.01C12.77 47.5 34 47.5 34 47.5s21.23 0 26.51-1.23c2.93-.77 5.23-3.08 6.01-6.01C67.75 34.97 67.75 24 67.75 24s0-10.97-1.23-16.26z" fill="#FF0000"/>
            <path d="M27.25 34.25L45.25 24l-18-10.25v20.5z" fill="#FFF"/>
          </svg>
        `;
        playIcon.style.opacity = '0.9';
        thumbnail.appendChild(playIcon);

        iframe.replaceWith(thumbnail);
      }
    }
  });

  // 오디오 요소를 아이콘으로 교체
  const audioElements = clonedContainer.querySelectorAll('audio');
  audioElements.forEach((audio) => {
    const parent = audio.parentElement;
    if (parent) {
      const audioPlaceholder = document.createElement('div');
      audioPlaceholder.style.display = 'flex';
      audioPlaceholder.style.alignItems = 'center';
      audioPlaceholder.style.justifyContent = 'center';
      audioPlaceholder.style.gap = '8px';
      audioPlaceholder.style.padding = '16px';
      audioPlaceholder.style.backgroundColor = '#f3f4f6';
      audioPlaceholder.style.borderRadius = '8px';
      audioPlaceholder.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2">
          <path d="M9 18V5l12-2v13"/>
          <circle cx="6" cy="18" r="3"/>
          <circle cx="18" cy="16" r="3"/>
        </svg>
        <span style="color: #6b7280; font-size: 14px;">Audio File</span>
      `;
      audio.replaceWith(audioPlaceholder);
    }
  });

  // cleanup 함수 생성
  const cleanup = () => {
    if (clonedContainer && clonedContainer.parentNode) {
      document.body.removeChild(clonedContainer);
    }
  };

  return {
    element: clonedContainer,
    cleanup,
  };
}

/**
 * 캔버스를 이미지로 캡처합니다.
 */
export async function captureCanvas(
  element: HTMLElement,
  options: {
    scale?: number;
    backgroundColor?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  } = {}
): Promise<HTMLCanvasElement> {
  const { scale = 2, backgroundColor = '#ffffff', x, y, width, height } = options;

  return html2canvas(element, {
    scale,
    backgroundColor,
    useCORS: true,
    allowTaint: true,
    logging: false,
    x,
    y,
    width,
    height,
    // 스크롤 문제 방지
    scrollX: 0,
    scrollY: 0,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });
}

/**
 * 캔버스를 JPG로 내보냅니다.
 */
export async function exportToJPG(
  element: HTMLElement,
  options: JPGExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<void> {
  try {
    onProgress?.({
      status: 'preparing',
      progress: 10,
      message: '내보내기 준비 중...',
    });

    const qualityValue = IMAGE_QUALITY_MAP[options.quality];

    onProgress?.({
      status: 'capturing',
      progress: 30,
      message: '캔버스 캡처 중...',
    });

    const canvas = await captureCanvas(element, {
      scale: options.scale,
      backgroundColor: options.backgroundColor,
    });

    onProgress?.({
      status: 'processing',
      progress: 70,
      message: '이미지 처리 중...',
    });

    const dataUrl = canvas.toDataURL('image/jpeg', qualityValue);

    onProgress?.({
      status: 'processing',
      progress: 90,
      message: '다운로드 준비 중...',
    });

    downloadFile(dataUrl, `${options.filename}.jpg`);

    onProgress?.({
      status: 'complete',
      progress: 100,
      message: '내보내기 완료!',
    });
  } catch (error) {
    onProgress?.({
      status: 'error',
      progress: 0,
      message: `내보내기 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
    });
    throw error;
  }
}

/**
 * 캔버스를 PDF로 내보냅니다.
 */
export async function exportToPDF(
  element: HTMLElement,
  options: PDFExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<void> {
  try {
    onProgress?.({
      status: 'preparing',
      progress: 10,
      message: '내보내기 준비 중...',
    });

    onProgress?.({
      status: 'capturing',
      progress: 30,
      message: '캔버스 캡처 중...',
    });

    // 고해상도로 캡처
    const canvas = await captureCanvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
    });

    onProgress?.({
      status: 'processing',
      progress: 60,
      message: 'PDF 생성 중...',
    });

    const imgData = canvas.toDataURL('image/jpeg', 1.0);

    // PDF 설정
    let pdfWidth: number;
    let pdfHeight: number;
    const margin = options.margin;

    if (options.paperSize === 'fit') {
      // 콘텐츠 크기에 맞춤
      const mmPerPixel = 0.264583; // 1px = 0.264583mm (96 DPI 기준)
      pdfWidth = (canvas.width / 2) * mmPerPixel + margin * 2;
      pdfHeight = (canvas.height / 2) * mmPerPixel + margin * 2;
    } else {
      // A4 or Letter
      const paperSizes = {
        A4: { width: 297, height: 210 }, // landscape
        Letter: { width: 279.4, height: 215.9 }, // landscape
      };

      if (options.orientation === 'portrait') {
        pdfWidth = paperSizes[options.paperSize].height;
        pdfHeight = paperSizes[options.paperSize].width;
      } else {
        pdfWidth = paperSizes[options.paperSize].width;
        pdfHeight = paperSizes[options.paperSize].height;
      }
    }

    const pdf = new jsPDF({
      orientation: options.orientation,
      unit: 'mm',
      format: options.paperSize === 'fit' ? [pdfWidth, pdfHeight] : options.paperSize.toLowerCase(),
    });

    // 이미지를 PDF에 맞게 조정
    const contentWidth = pdfWidth - margin * 2;
    const contentHeight = pdfHeight - margin * 2;

    // 이미지의 종횡비 계산
    const imgAspectRatio = canvas.width / canvas.height;
    const contentAspectRatio = contentWidth / contentHeight;

    let imgWidth: number;
    let imgHeight: number;
    let imgX: number;
    let imgY: number;

    if (imgAspectRatio > contentAspectRatio) {
      // 이미지가 더 넓음 - 너비에 맞춤
      imgWidth = contentWidth;
      imgHeight = contentWidth / imgAspectRatio;
      imgX = margin;
      imgY = margin + (contentHeight - imgHeight) / 2;
    } else {
      // 이미지가 더 높음 - 높이에 맞춤
      imgHeight = contentHeight;
      imgWidth = contentHeight * imgAspectRatio;
      imgX = margin + (contentWidth - imgWidth) / 2;
      imgY = margin;
    }

    onProgress?.({
      status: 'processing',
      progress: 80,
      message: 'PDF 저장 중...',
    });

    pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth, imgHeight);

    onProgress?.({
      status: 'processing',
      progress: 95,
      message: '다운로드 준비 중...',
    });

    pdf.save(`${options.filename}.pdf`);

    onProgress?.({
      status: 'complete',
      progress: 100,
      message: '내보내기 완료!',
    });
  } catch (error) {
    onProgress?.({
      status: 'error',
      progress: 0,
      message: `내보내기 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
    });
    throw error;
  }
}

/**
 * 파일 다운로드 헬퍼 함수
 */
function downloadFile(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * 내보내기 실행 (통합 함수)
 */
export async function executeExport(
  element: HTMLElement,
  options: ExportOptions,
  onProgress?: (progress: ExportProgress) => void
): Promise<void> {
  if (options.format === 'pdf') {
    await exportToPDF(element, options as PDFExportOptions, onProgress);
  } else {
    await exportToJPG(element, options as JPGExportOptions, onProgress);
  }
}

/**
 * 현재 날짜를 기반으로 기본 파일명을 생성합니다.
 */
export function generateDefaultFilename(): string {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10);
  const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
  return `notesense-${dateStr}-${timeStr}`;
}
